/* eslint-disable complexity */
import { Box, Button, Stack, useTheme } from '@mui/material';
import {
	INewAccount,
	NewAccountCreationSource,
	NewAccountStatus,
	NewBankAccountStatus,
	newAccountsSteps,
} from 'constants/new_accounts.constants';
import { Fragment, useEffect, useRef, useState } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import _ from 'lodash';
import { CustomerDetails } from '../Steps/customerDetails';
import { UserDetails } from '../Steps/userDetails';
import { BankAccountDetails } from '../Steps/bankAccountDetails';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	getActiveStepIdx,
	getFinishProcess,
	getNewAccount,
	getNewBankAccounts,
	getNewUsers,
	getNewUser,
	getIsDirty,
	setActiveStepIdx,
	setNewBankAccount,
	setNewUser,
	getNewBankAccount,
	getIsLoading,
} from 'state/reducers/new_accounts.reducers';
import { pagesEnum } from 'constants/ui.constants';
import { useNavigate, useParams } from 'react-router-dom';
import { FinalApprove } from '../Steps/approveFinal';
import { getTabTitle } from './helper';
import { TabsStack } from 'theme/elements/Content';
import { NewAccountTabs } from './elements';
import { NewUserStatus } from 'constants/new_accounts.constants';
import { RoundProgressBar } from 'components/RoundProgressBar/';
import { Page } from 'theme/elements/Page';

interface IStepperProps {
	newAccounts?: INewAccount[];
	currentNewAccountIdx?: number;
	currentNewUserIdx?: number;
	currentNewBankAccountIdx?: number;
	isApproveMode?: boolean;
}

export const Stepper = (props: IStepperProps) => {
	const { isApproveMode } = props;
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const { action } = useParams();
	const navigate = useNavigate();
	const [stepsAvailable, setStepsAvailable] = useState<boolean[]>([true, false, false]);
	const [ifStepPassed, setIfStepPassed] = useState<Record<number, boolean>>({
		1: false,
		2: false,
		3: false,
	});
	const dispatch = useAppDispatch();
	const newAccount = useAppSelector(getNewAccount);
	const newUsers = useAppSelector(getNewUsers);
	const newUser = useAppSelector(getNewUser);
	const newBankAccounts = useAppSelector(getNewBankAccounts);
	const newBankAccount = useAppSelector(getNewBankAccount);
	const activeStepIdx = useAppSelector(getActiveStepIdx);
	const finishProcess = useAppSelector(getFinishProcess);
	const isDirty = useAppSelector(getIsDirty);
	const isLoading = useAppSelector(getIsLoading);
	const userChipRef = useRef<any>(null);
	const isHyperRecoveryReview = isApproveMode && newAccount?.creationSource === NewAccountCreationSource.HyperWebhook;

	useEffect(() => {
		if (isHyperRecoveryReview || _.values(newBankAccounts).length > 0) {
			setStepsAvailable([true, true, true]);
		}
	}, [isHyperRecoveryReview, newBankAccounts]);

	useEffect(() => {
		if (finishProcess) {
			navigate(`/${pagesEnum.ACCOUNTS}`);
		}
	}, [finishProcess]);

	useEffect(() => {
		setIfStepPassed({
			1: _.find(newAccount, 'createdAt') || false,
			2: _.find(newUsers, 'createdAt') || false,
			3: _.find(newBankAccounts, 'createdAt') || false,
		});
	}, [newUsers, newBankAccounts, newAccount]);

	const GetCheckedSign = (index: number, item?: Record<string, any>) => {
		if (index === 1 && newAccount?.status === NewAccountStatus.APPROVED) {
			return <CheckCircleRoundedIcon />;
		}

		if (index === 2) {
			if (item && item.status === NewUserStatus.APPROVED) {
				return <CheckCircleRoundedIcon />;
			}
			if (!item && _.keys(newUsers).length > 0 && isApproveMode) {
				return (
					<RoundProgressBar
						step={_.filter(_.values(newUsers), { status: NewUserStatus.APPROVED }).length}
						total={_.keys(newUsers).length}
					/>
				);
			}
		}

		if (index === 3) {
			if (item && item.status === NewBankAccountStatus.APPROVED) {
				return <CheckCircleRoundedIcon />;
			}
			if (!item && _.keys(newBankAccounts).length > 0 && isApproveMode) {
				return (
					<RoundProgressBar
						step={_.filter(_.values(newBankAccounts), { status: NewBankAccountStatus.APPROVED }).length}
						total={_.keys(newBankAccounts).length}
					/>
				);
			}
		}
	};

	useEffect(() => {
		if (activeStepIdx === 2 && Object.keys(newUsers).length > 0 && userChipRef) {
			userChipRef?.current?.click();
		}
	}, [activeStepIdx, newUsers, userChipRef]);

	return (
		<Page loading={isLoading}>
			<Fragment>
				<NewAccountTabs>
					{_.values(newAccountsSteps).map((step, idx) => (
						<Button
							key={idx}
							className={`mainTab ${activeStepIdx === idx + 1 && 'selected'} ${
								isApproveMode ? (idx + 1 === 4 ? ' last' : '') : idx + 1 === 3 ? ' last' : ''
							}`}
							style={{
								display:
									step !== newAccountsSteps.CONCLUSION_AND_APPROVE ||
									(isApproveMode && step === newAccountsSteps.CONCLUSION_AND_APPROVE)
										? 'flex'
										: 'none',
							}}
							disabled={!stepsAvailable[idx] || isDirty.dirty}
							onClick={() => {
								if (newAccount?.status !== NewAccountStatus.IN_REVIEW || isHyperRecoveryReview) {
									dispatch(setActiveStepIdx(idx + 1));
								}
							}}
						>
							<Stack>
								<div>{getTabTitle(t, step, newAccount)}</div>
							</Stack>
							{step === newAccountsSteps.USER_DETAILS && (
								<TabsStack>
									{_.values(newUsers).map((user, index) => (
										<Button
											ref={userChipRef}
											key={index + 1000}
											disabled={isApproveMode ? false : activeStepIdx !== 2}
											onClick={() => {
												dispatch(setNewUser({ ...user }));
											}}
											style={{
												borderRadius: '4px',
												backgroundColor:
													activeStepIdx === 2
														? user.id === newUser?.id
															? '#C7E5DE'
															: '#85A09E'
														: '#85A09E',
											}}
											endIcon={GetCheckedSign(2, user)}
										>
											{`${user?.firstNameHebrew || ''} ${user?.lastNameHebrew || ''}`}
										</Button>
									))}
								</TabsStack>
							)}
							{step === newAccountsSteps.BANK_ACCOUNT_DETAILS && (
								<TabsStack>
									{_.values(newBankAccounts).map((bankAccount, indx) => (
										<Button
											key={indx + 200}
											//disabled={isApproveMode ? false : activeStepIdx !== 3}
											onClick={() => {
												dispatch(setNewBankAccount({ ...bankAccount }));
											}}
											style={{
												borderRadius: '4px',
												backgroundColor:
													activeStepIdx === 3
														? bankAccount.id === newBankAccount?.id
															? '#C7E5DE'
															: '#85A09E'
														: '#85A09E',
											}}
											endIcon={GetCheckedSign(3, bankAccount)}
										>
											{bankAccount?.accountOwnerName}
										</Button>
									))}
								</TabsStack>
							)}
							{GetCheckedSign(idx + 1)}
						</Button>
					))}
				</NewAccountTabs>
				<Box>
					{activeStepIdx === 1 ? (
						<CustomerDetails
							isApproveMode={isApproveMode}
							handleForward={() => {
								dispatch(setActiveStepIdx(activeStepIdx + 1));
								const steps = stepsAvailable;
								steps[activeStepIdx] = true;
								setStepsAvailable(steps);
							}}
						/>
					) : activeStepIdx === 2 ? (
						<UserDetails
							isApproveMode={isApproveMode}
							handleBack={() => dispatch(setActiveStepIdx(activeStepIdx - 1))}
							handleForward={() =>
								_.some(newUsers, { new_account_id: newAccount?.id }) &&
								dispatch(setActiveStepIdx(activeStepIdx + 1))
							}
						/>
					) : activeStepIdx === 3 ? (
						<BankAccountDetails
							isApproveMode={isApproveMode}
							handleForward={() => dispatch(setActiveStepIdx(activeStepIdx + 1))}
							handleBack={() => {
								dispatch(setNewBankAccount({}));
								dispatch(setActiveStepIdx(activeStepIdx - 1));
							}}
						/>
					) : activeStepIdx === 4 ? (
						<FinalApprove handleBack={() => dispatch(setActiveStepIdx(activeStepIdx - 1))} />
					) : (
						<></>
					)}
				</Box>
			</Fragment>
		</Page>
	);
};
